import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, canonicalStoredFileIdForApiPath } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import { formatMenuInstant } from '../lib/date'

const ROLE_LABELS = {
  CONTRACT_HOLDER: '계약자',
  INSURED: '피보험자',
}

function unwrap(res) {
  return res?.data?.data !== undefined ? res.data.data : res.data
}

function signatureFor(session, role) {
  return (session?.signatures || []).find((s) => s.signerRole === role) || null
}

function statusLabel(status) {
  const s = String(status || '').toUpperCase()
  if (s === 'COMPLETED') return '서명/자필 완료'
  if (s === 'REQUESTED') return '링크 발송됨'
  if (s === 'EXPIRED') return '만료'
  if (s === 'PENDING') return '대기'
  return '미서명'
}

function signatureModeLabel(mode) {
  if (mode === 'REMOTE') return '링크발송'
  if (mode === 'IN_PERSON') return '대면서명'
  if (mode === 'PRINT') return '자필서명'
  return '미선택'
}

/** 링크 발송(REMOTE) 시 알림톡 수신 번호 */
function phoneForRemoteRole(form, role) {
  if (role === 'CONTRACT_HOLDER') return form.contractorPhone
  if (form.insuredSameAsContractor) return form.contractorPhone
  return form.insuredPhone
}

/** 작성 탭 단계 (다른 업무 화면과 동일한 STEP 패턴) */
const WRITE_WIZARD_STEPS = [
  { id: 1, title: '계약정보' },
  { id: 2, title: '고객·서명' },
  { id: 3, title: '생성' },
]

/** 백엔드 AfterContractCertificateRenderService.EXTRA_CONTRACT_LINES 와 동일 */
const EXTRA_AFTER_CONTRACT_LINES = 'afterContractLines'

function todayDateInputValue(date = new Date()) {
  const yyyy = String(date.getFullYear())
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function normalizeDateInputValue(value) {
  const s = String(value || '').trim()
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : ''
}

function emptyContractLine(printedAt = '') {
  return {
    contractId: '',
    policyNumber: '',
    designNumber: '',
    insuranceCompanyName: '',
    productName: '',
    printedAt: normalizeDateInputValue(printedAt),
  }
}

function normalizeLinesForSave(lines, printedAt = '') {
  const defaultPrintedAt = normalizeDateInputValue(printedAt)
  const raw = Array.isArray(lines) && lines.length > 0 ? lines : [emptyContractLine(defaultPrintedAt)]
  const normalized = raw.map((row) => ({
    contractId: (() => {
      const v = row?.contractId
      if (v === '' || v == null) return null
      const n = Number(v)
      return Number.isFinite(n) ? n : null
    })(),
    policyNumber: String(row?.policyNumber ?? '').trim(),
    designNumber: String(row?.designNumber ?? '').trim(),
    insuranceCompanyName: String(row?.insuranceCompanyName ?? '').trim(),
    productName: String(row?.productName ?? '').trim(),
    printedAt: normalizeDateInputValue(row?.printedAt || row?.documentDate) || defaultPrintedAt,
  }))
  const filtered = normalized.filter((row) =>
    [row.policyNumber, row.designNumber, row.insuranceCompanyName, row.productName].some((v) => v && String(v).trim() !== ''),
  )
  return filtered.length > 0 ? filtered : normalized.slice(0, 1)
}

function linesFromSessionExtra(selected) {
  const extra = selected?.extraFields || {}
  const defaultPrintedAt = normalizeDateInputValue(extra.printedAt || extra.documentDate)
  if (Array.isArray(extra[EXTRA_AFTER_CONTRACT_LINES]) && extra[EXTRA_AFTER_CONTRACT_LINES].length > 0) {
    return extra[EXTRA_AFTER_CONTRACT_LINES].map((r) => ({
      contractId: r?.contractId != null ? String(r.contractId) : '',
      policyNumber: String(r?.policyNumber ?? ''),
      designNumber: String(r?.designNumber ?? ''),
      insuranceCompanyName: String(r?.insuranceCompanyName ?? ''),
      productName: String(r?.productName ?? ''),
      printedAt: normalizeDateInputValue(r?.printedAt || r?.documentDate) || defaultPrintedAt,
    }))
  }
  return [
    {
      contractId: selected?.contractId != null ? String(selected.contractId) : '',
      policyNumber: String(selected?.policyNumber ?? ''),
      designNumber: String(selected?.designNumber ?? ''),
      insuranceCompanyName: String(selected?.insuranceCompanyName ?? ''),
      productName: String(selected?.productName ?? ''),
      printedAt: defaultPrintedAt,
    },
  ]
}

function contractLineCountForDownload(session) {
  const lines = session?.extraFields?.[EXTRA_AFTER_CONTRACT_LINES]
  if (!Array.isArray(lines) || lines.length === 0) return 1
  const n = lines.filter((row) =>
    row &&
    ['policyNumber', 'designNumber', 'insuranceCompanyName', 'productName'].some(
      (k) => row[k] != null && String(row[k]).trim() !== '',
    ),
  ).length
  return n > 0 ? n : 1
}

function initialForm(searchParams) {
  const printedAt = todayDateInputValue()
  return {
    customerRefId: searchParams.get('customerRefId') || '',
    contractId: searchParams.get('contractId') || '',
    applicationNumber: '',
    policyNumber: '',
    designNumber: '',
    insuranceCompanyName: '',
    productName: '',
    contractorName: '',
    contractorPhone: '',
    insuredSameAsContractor: true,
    insuredName: '',
    insuredPhone: '',
    plannerHandwriting_explanation: '',
    plannerHandwriting_confirm: '',
    printedAt,
    afterContractLines: [emptyContractLine(printedAt)],
  }
}

export default function AfterContractCertificateSessionsPage({ embedded = false }) {
  const { show } = useToast()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [sessions, setSessions] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [selected, setSelected] = useState(null)
  const [form, setForm] = useState(() => initialForm(searchParams))
  const [activeTab, setActiveTab] = useState('write')
  const [writeStep, setWriteStep] = useState(1)

  useEffect(() => {
    setWriteStep((w) => (w > WRITE_WIZARD_STEPS.length ? WRITE_WIZARD_STEPS.length : w))
  }, [])
  const [signatureModeDraft, setSignatureModeDraft] = useState({})
  const [plannerPreviewUrls, setPlannerPreviewUrls] = useState({ explanation: '', confirm: '' })
  const [plannerEditing, setPlannerEditing] = useState(false)
  const [step3SignaturePreview, setStep3SignaturePreview] = useState({ loading: false, items: [] })
  const [signatureStatusPersonTab, setSignatureStatusPersonTab] = useState('CONTRACT_HOLDER')
  const [busy, setBusy] = useState(false)
  const [signing, setSigning] = useState(null)
  const canvasRef = useRef(null)
  const ctxRef = useRef(null)
  const drawingRef = useRef(false)
  const hasInkRef = useRef(false)
  const handwritingCanvasRef = useRef(null)
  const handwritingCtxRef = useRef(null)
  const handwritingDrawingRef = useRef(false)
  const handwritingHasInkRef = useRef(false)
  const insuredHandwritingCanvasRef = useRef(null)
  const insuredHandwritingCtxRef = useRef(null)
  const insuredHandwritingDrawingRef = useRef(false)
  const insuredHandwritingHasInkRef = useRef(false)
  const plannerExplanationCanvasRef = useRef(null)
  const plannerConfirmCanvasRef = useRef(null)
  const plannerExplanationCtxRef = useRef(null)
  const plannerConfirmCtxRef = useRef(null)
  const plannerExplanationDrawingRef = useRef(false)
  const plannerConfirmDrawingRef = useRef(false)
  const plannerExplanationHasInkRef = useRef(false)
  const plannerConfirmHasInkRef = useRef(false)

  const selectedSession = useMemo(() => selected || sessions.find((s) => s.id === selectedId) || null, [selected, sessions, selectedId])

  const step3PreviewSourceKey = useMemo(() => {
    if (!selectedSession?.id) return ''
    const ex = selectedSession.extraFields || {}
    const sigs = selectedSession.signatures || []
    const sigPart = sigs
      .map((s) => `${s.signerRole}:${s.status}:${s.mode || ''}:${s.signatureFileId || ''}:${s.handwritingFileId || ''}`)
      .join('|')
    return [
      selectedSession.id,
      ex.insuredSameAsContractor,
      ex.insuredHandwriting_disclosure || '',
      ex.insuredHandwriting_sincereDisclosure || '',
      sigPart,
    ].join('~')
  }, [selectedSession])

  useEffect(() => {
    let ignore = false
    const objectUrls = []
    const loadPreview = async (fileId) => {
      if (!fileId) return ''
      const pathId = canonicalStoredFileIdForApiPath(fileId)
      try {
        const res = await api.get(`/files/${encodeURIComponent(pathId)}/preview`, {
          responseType: 'blob',
          __silent: true,
        })
        if (ignore) return ''
        const url = URL.createObjectURL(res.data)
        objectUrls.push(url)
        return url
      } catch {
        return ''
      }
    }
    ;(async () => {
      const [explanation, confirm] = await Promise.all([
        loadPreview(form.plannerHandwriting_explanation),
        loadPreview(form.plannerHandwriting_confirm),
      ])
      if (!ignore) setPlannerPreviewUrls({ explanation, confirm })
    })()
    return () => {
      ignore = true
      objectUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [form.plannerHandwriting_explanation, form.plannerHandwriting_confirm])

  useEffect(() => {
    if (writeStep !== 2 || !selectedSession?.id) {
      setStep3SignaturePreview((prev) => {
        ;(prev.items || []).forEach((item) => {
          if (item?.url) URL.revokeObjectURL(item.url)
        })
        return { loading: false, items: [] }
      })
      return undefined
    }

    let cancelled = false
    const objectUrls = []

    const run = async () => {
      setStep3SignaturePreview((prev) => {
        ;(prev.items || []).forEach((item) => {
          if (item?.url) URL.revokeObjectURL(item.url)
        })
        return { loading: true, items: [] }
      })

      const extra = selectedSession.extraFields || {}
      const samePerson = Boolean(extra.insuredSameAsContractor ?? form.insuredSameAsContractor)
      const contractorSig = signatureFor(selectedSession, 'CONTRACT_HOLDER')
      const insuredSig = signatureFor(selectedSession, 'INSURED')
      const candidates = [
        { label: samePerson ? '계약자/피보험자 서명' : '계약자 서명', fileId: contractorSig?.signatureFileId },
        { label: '계약자 자필: 설명을 듣고 이해', fileId: contractorSig?.handwritingFileId },
        ...(samePerson ? [] : [{ label: '피보험자 서명', fileId: insuredSig?.signatureFileId }]),
        {
          label: '피보험자 자필: 성실히 고지',
          fileId: samePerson
            ? (extra.insuredHandwriting_disclosure || extra.insuredHandwriting_sincereDisclosure)
            : insuredSig?.handwritingFileId,
        },
      ].filter((item) => item?.fileId)

      const loaded = []
      for (const item of candidates) {
        if (cancelled) break
        try {
          const pathId = canonicalStoredFileIdForApiPath(item.fileId)
          const res = await api.get(`/files/${encodeURIComponent(pathId)}/preview`, {
            responseType: 'blob',
            __silent: true,
          })
          const url = URL.createObjectURL(res.data)
          objectUrls.push(url)
          loaded.push({ label: item.label, url })
        } catch {
          loaded.push({ label: item.label, url: '' })
        }
      }

      if (cancelled) {
        objectUrls.forEach((u) => URL.revokeObjectURL(u))
        return
      }

      setStep3SignaturePreview({ loading: false, items: loaded })
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [writeStep, step3PreviewSourceKey, form.insuredSameAsContractor, selectedSession])

  const loadSessions = useCallback(async () => {
    const res = await api.get('/after-contract/certificates')
    const rows = unwrap(res)
    setSessions(Array.isArray(rows) ? rows : [])
  }, [])

  const loadSession = useCallback(async (id) => {
    if (!id) return
    const res = await api.get(`/after-contract/certificates/${id}`)
    const payload = unwrap(res)
    setSelected(payload)
    setSelectedId(payload?.id || id)
  }, [])

  useEffect(() => {
    loadSessions().catch(() => {})
  }, [loadSessions])

  useEffect(() => {
    const id = searchParams.get('sessionId')
    if (!id) return
    setWriteStep(1)
    loadSession(id).catch(() => {})
  }, [searchParams, loadSession])

  useEffect(() => {
    const customerRefId = searchParams.get('customerRefId')
    const contractId = searchParams.get('contractId')
    if (!customerRefId || !contractId) return
    ;(async () => {
      try {
        const res = await api.get(`/customer-management/customers/${customerRefId}/coverage-contracts`, { __silent: true })
        const payload = unwrap(res)
        const contracts = Array.isArray(payload?.contracts) ? payload.contracts : []
        const c = contracts.find((x) => String(x.id) === String(contractId))
        if (!c) return
        setForm((prev) => {
          const rows =
            Array.isArray(prev.afterContractLines) && prev.afterContractLines.length > 0
              ? [...prev.afterContractLines]
              : [emptyContractLine()]
          rows[0] = {
            ...rows[0],
            contractId: String(c.id),
            policyNumber: c.policyNumberMasked || rows[0].policyNumber,
            insuranceCompanyName: c.insurerName || rows[0].insuranceCompanyName,
            productName: c.productName || rows[0].productName,
            printedAt: rows[0].printedAt || prev.printedAt || todayDateInputValue(),
          }
          return {
            ...prev,
            customerRefId,
            contractId: String(c.id),
            afterContractLines: rows,
            policyNumber: c.policyNumberMasked || prev.policyNumber,
            insuranceCompanyName: c.insurerName || prev.insuranceCompanyName,
            productName: c.productName || prev.productName,
          }
        })
      } catch {}
    })()
  }, [searchParams])

  useEffect(() => {
    ;(async () => {
      try {
        const res = await api.get('/users/me', { __silent: true })
        const me = unwrap(res) || {}
        setForm((prev) => ({
          ...prev,
          plannerHandwriting_explanation: prev.plannerHandwriting_explanation || me.afterContractExplanationFileId || '',
          plannerHandwriting_confirm: prev.plannerHandwriting_confirm || me.afterContractConfirmFileId || '',
        }))
      } catch {}
    })()
  }, [])

  const buildSessionPayload = (extraOverrides = {}) => {
    const {
      plannerHandwriting_explanation,
      plannerHandwriting_confirm,
      insuredSameAsContractor,
      printedAt,
      afterContractLines,
      ...restForm
    } = form

    const normalizedPrintedAt = normalizeDateInputValue(printedAt)
    const lines = normalizeLinesForSave(afterContractLines, normalizedPrintedAt)
    const first = lines[0] || emptyContractLine()
    const samePerson = Boolean(insuredSameAsContractor)

    const baseForm = {
      ...restForm,
      contractId: first.contractId != null ? String(first.contractId) : restForm.contractId || '',
      applicationNumber: first.policyNumber || restForm.policyNumber || restForm.applicationNumber,
      policyNumber: first.policyNumber || restForm.policyNumber,
      designNumber: first.designNumber || restForm.designNumber,
      insuranceCompanyName: first.insuranceCompanyName || restForm.insuranceCompanyName,
      productName: first.productName || restForm.productName,
    }

    if (samePerson) {
      baseForm.insuredName = baseForm.contractorName
      baseForm.insuredPhone = baseForm.contractorPhone
    }
    const payload = Object.fromEntries(Object.entries(baseForm).map(([k, v]) => [k, v === '' ? null : v]))
    payload.applicationNumber = payload.applicationNumber || payload.policyNumber
    if (payload.customerRefId === '' || payload.customerRefId == null) {
      payload.customerRefId = null
    } else {
      const cr = Number(payload.customerRefId)
      payload.customerRefId = Number.isFinite(cr) ? cr : null
    }
    if (payload.contractId === '' || payload.contractId == null) {
      payload.contractId = null
    } else {
      const cid = Number(payload.contractId)
      payload.contractId = Number.isFinite(cid) ? cid : null
    }

    payload.extraFields = {
      ...(selectedSession?.extraFields || {}),
      insuredSameAsContractor: samePerson,
      [EXTRA_AFTER_CONTRACT_LINES]: lines,
      plannerHandwriting_explanation: plannerHandwriting_explanation || null,
      plannerHandwriting_confirm: plannerHandwriting_confirm || null,
      customerHandwriting_explanation: plannerHandwriting_explanation || null,
      customerHandwriting_confirm: plannerHandwriting_confirm || null,
      contractorHandwriting_explanation: plannerHandwriting_explanation || null,
      contractorHandwriting_confirm: plannerHandwriting_confirm || null,
      insuredHandwriting_explanation: plannerHandwriting_explanation || null,
      insuredHandwriting_confirm: plannerHandwriting_confirm || null,
      printedAt: normalizedPrintedAt || null,
      ...extraOverrides,
    }
    return payload
  }

  const persistSession = async (extraOverrides = {}) => {
      const payload = buildSessionPayload()
      if (extraOverrides && Object.keys(extraOverrides).length > 0) {
        payload.extraFields = { ...(payload.extraFields || {}), ...extraOverrides }
      }
      const res = selectedSession?.id
        ? await api.put(`/after-contract/certificates/${selectedSession.id}`, payload)
        : await api.post('/after-contract/certificates', payload)
      const saved = unwrap(res)
      setSelected(saved)
      setSelectedId(saved.id)
      await loadSessions()
      return saved
  }

  const goToMyProfileAfterAutosave = async () => {
    setBusy(true)
    try {
      await persistSession()
      show?.('내 정보로 이동합니다. 현재까지 작성된 정보는 자동 저장됩니다.', 'success')
      navigate('/profile')
    } catch {
      show?.('자동 저장에 실패했습니다. 입력값을 확인한 뒤 다시 시도해 주세요.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const saveSession = async () => {
    setBusy(true)
    try {
      await persistSession()
      show?.('3종확인서 작성 세션을 저장했습니다.', 'success')
    } catch (e) {
      show?.('저장에 실패했습니다.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const setSignatureCollectionMode = async (role, mode) => {
    if (role === 'INSURED' && form.insuredSameAsContractor) {
      show?.('계약자/피보험자 동일 체크 시 피보험자는 계약자 서명 방식으로 함께 처리됩니다.', 'error')
      return
    }
    const prevModes = selectedSession?.extraFields?.signatureCollectionModes || {}
    const nextModes = { ...prevModes, [role]: mode }
    setSignatureModeDraft(nextModes)
    setBusy(true)
    try {
      await persistSession({ signatureCollectionModes: nextModes })
    } catch {
      show?.('서명 방식 저장에 실패했습니다.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const savePlannerPreset = async () => {
    setBusy(true)
    try {
      let explanationFileId = form.plannerHandwriting_explanation || ''
      let confirmFileId = form.plannerHandwriting_confirm || ''
      if (plannerExplanationHasInkRef.current) {
        explanationFileId = await uploadCanvasImage(plannerExplanationCanvasRef.current, 'after-contract-planner-explanation.png')
      }
      if (plannerConfirmHasInkRef.current) {
        confirmFileId = await uploadCanvasImage(plannerConfirmCanvasRef.current, 'after-contract-planner-confirm.png')
      }
      if (!explanationFileId || !confirmFileId) {
        show?.('설명/확인 자필을 모두 작성해 주세요.', 'error')
        return
      }
      await api.put('/users/me', {
        afterContractExplanationFileId: explanationFileId,
        afterContractConfirmFileId: confirmFileId,
      })
      setForm((prev) => ({
        ...prev,
        plannerHandwriting_explanation: explanationFileId,
        plannerHandwriting_confirm: confirmFileId,
      }))
      plannerExplanationHasInkRef.current = false
      plannerConfirmHasInkRef.current = false
      setPlannerEditing(false)
      show?.('설명/확인 자필을 내 계정에 저장했습니다.', 'success')
    } catch {
      show?.('자필 저장에 실패했습니다.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const loadPlannerPreset = async () => {
    setBusy(true)
    try {
      const res = await api.get('/users/me')
      const me = unwrap(res) || {}
      const explanation = me.afterContractExplanationFileId || ''
      const confirm = me.afterContractConfirmFileId || ''
      if (!explanation && !confirm) {
        show?.('저장된 설명/확인 자필이 없습니다.', 'error')
        return
      }
      setForm((prev) => ({
        ...prev,
        plannerHandwriting_explanation: explanation,
        plannerHandwriting_confirm: confirm,
      }))
      setPlannerEditing(false)
      show?.('저장된 설명/확인 자필을 불러왔습니다.', 'success')
    } catch {
      show?.('자필 불러오기에 실패했습니다.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const prepareSignature = async (role, mode, channel = 'KAKAO') => {
    if (role === 'INSURED' && form.insuredSameAsContractor) {
      show?.('계약자/피보험자 동일 체크 시 피보험자 서명은 계약자 서명으로 자동 적용됩니다.', 'error')
      return
    }
    if (mode === 'REMOTE') {
      const phone = phoneForRemoteRole(form, role)
      if (!String(phone || '').trim()) {
        show?.(`${ROLE_LABELS[role]} 휴대폰 번호를 입력한 뒤 링크 발송을 진행해 주세요.`, 'error')
        return
      }
    }
    setBusy(true)
    try {
      const prevModes = selectedSession?.extraFields?.signatureCollectionModes || {}
      const nextModes = { ...prevModes, [role]: mode }
      setSignatureModeDraft(nextModes)
      const savedSession = await persistSession({ signatureCollectionModes: nextModes })
      const res = await api.post(`/after-contract/certificates/${savedSession.id}/signatures`, {
        signerRole: role,
        mode,
        channel,
      })
      const payload = unwrap(res)
      await loadSession(savedSession.id)
      if (mode === 'REMOTE') {
        show?.(`${ROLE_LABELS[role]} 서명 링크를 발송했습니다.`, 'success')
        if (payload?.url) {
          try { await navigator.clipboard?.writeText(payload.url) } catch {}
        }
      } else {
        setSigning({ role, signatureId: payload.id })
        setTimeout(setupCanvas, 50)
      }
    } catch {
      show?.('서명 세션 생성에 실패했습니다. 입력값을 확인해 주세요.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const handwritingLabel = (role) => role === 'CONTRACT_HOLDER' ? '설명을 듣고 이해' : '성실히 고지'

  const uploadCanvasImage = async (canvas, filename) => {
    if (!canvas) throw new Error('canvas missing')
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!blob) throw new Error('canvas blob missing')
    const file = new File([blob], filename, { type: 'image/png' })
    const formData = new FormData()
    formData.append('file', file)
    const res = await api.post('/files', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    const payload = unwrap(res)
    const fileId = payload?.fileId || payload?.id
    if (!fileId) throw new Error('fileId missing')
    return fileId
  }

  const setupCanvasElement = (canvas, ctxTargetRef, opts = {}) => {
    if (!canvas) return
    const w = canvas.parentElement?.clientWidth || 320
    const minH = opts.minHeight ?? 150
    const maxH = opts.maxHeight ?? 260
    const ratio = opts.heightRatio ?? 0.42
    const h = Math.max(minH, Math.min(maxH, Math.round(w * ratio)))
    const dpr = window.devicePixelRatio || 1
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    canvas.width = Math.floor(w * dpr)
    canvas.height = Math.floor(h * dpr)
    const ctx = canvas.getContext('2d')
    ctxTargetRef.current = ctx
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = 3
    ctx.strokeStyle = '#0f172a'
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, w, h)
  }

  const setupCanvas = () => {
    const signingCanvasOpts = { minHeight: 120, maxHeight: 210, heightRatio: 0.34 }
    setupCanvasElement(canvasRef.current, ctxRef, signingCanvasOpts)
    setupCanvasElement(handwritingCanvasRef.current, handwritingCtxRef, signingCanvasOpts)
    setupCanvasElement(insuredHandwritingCanvasRef.current, insuredHandwritingCtxRef, signingCanvasOpts)
    hasInkRef.current = false
    handwritingHasInkRef.current = false
    insuredHandwritingHasInkRef.current = false
  }

  const setupPlannerCanvases = () => {
    setupCanvasElement(plannerExplanationCanvasRef.current, plannerExplanationCtxRef)
    setupCanvasElement(plannerConfirmCanvasRef.current, plannerConfirmCtxRef)
    plannerExplanationHasInkRef.current = false
    plannerConfirmHasInkRef.current = false
  }

  useEffect(() => {
    if (!plannerEditing) return undefined
    const t = setTimeout(setupPlannerCanvases, 50)
    const onResize = () => setupPlannerCanvases()
    window.addEventListener('resize', onResize)
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', onResize)
    }
  }, [plannerEditing])

  const clearCanvas = () => {
    const reset = (canvas, ctx) => {
      if (!canvas || !ctx) return
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    }
    reset(canvasRef.current, ctxRef.current)
    reset(handwritingCanvasRef.current, handwritingCtxRef.current)
    reset(insuredHandwritingCanvasRef.current, insuredHandwritingCtxRef.current)
    hasInkRef.current = false
    handwritingHasInkRef.current = false
    insuredHandwritingHasInkRef.current = false
  }

  const clearHandwritingCanvas = () => {
    const canvas = handwritingCanvasRef.current
    const ctx = handwritingCtxRef.current
    if (!canvas || !ctx) return
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    handwritingHasInkRef.current = false
  }

  const clearInsuredHandwritingCanvas = () => {
    const canvas = insuredHandwritingCanvasRef.current
    const ctx = insuredHandwritingCtxRef.current
    if (!canvas || !ctx) return
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    insuredHandwritingHasInkRef.current = false
  }

  const point = (e, ref = canvasRef) => {
    const rect = ref.current.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const onPlannerHandwritingDown = (e, canvasRefArg, ctxRefArg, drawingRefArg, hasInkRefArg) => {
    e.preventDefault()
    const ctx = ctxRefArg.current
    if (!ctx) return
    drawingRefArg.current = true
    const p = point(e, canvasRefArg)
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + 0.01, p.y + 0.01)
    ctx.stroke()
    hasInkRefArg.current = true
  }

  const onPlannerHandwritingMove = (e, canvasRefArg, ctxRefArg, drawingRefArg) => {
    if (!drawingRefArg.current || !ctxRefArg.current) return
    e.preventDefault()
    const p = point(e, canvasRefArg)
    ctxRefArg.current.lineTo(p.x, p.y)
    ctxRefArg.current.stroke()
  }

  const endPlannerHandwriting = (ctxRefArg, drawingRefArg) => {
    drawingRefArg.current = false
    try { ctxRefArg.current?.closePath() } catch {}
  }

  const clearPlannerCanvas = (canvasRefArg, ctxRefArg, hasInkRefArg, key) => {
    const canvas = canvasRefArg.current
    const ctx = ctxRefArg.current
    if (!canvas || !ctx) return
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight)
    hasInkRefArg.current = false
    if (key) setForm((prev) => ({ ...prev, [key]: '' }))
  }

  const onDown = (e) => {
    e.preventDefault()
    const ctx = ctxRef.current
    if (!ctx) return
    drawingRef.current = true
    const p = point(e)
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + 0.01, p.y + 0.01)
    ctx.stroke()
    hasInkRef.current = true
  }

  const onMove = (e) => {
    if (!drawingRef.current || !ctxRef.current) return
    e.preventDefault()
    const p = point(e)
    ctxRef.current.lineTo(p.x, p.y)
    ctxRef.current.stroke()
  }

  const end = () => {
    drawingRef.current = false
    try { ctxRef.current?.closePath() } catch {}
  }

  const onHandwritingDown = (e) => {
    e.preventDefault()
    const ctx = handwritingCtxRef.current
    if (!ctx) return
    handwritingDrawingRef.current = true
    const p = point(e, handwritingCanvasRef)
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + 0.01, p.y + 0.01)
    ctx.stroke()
    handwritingHasInkRef.current = true
  }

  const onInsuredHandwritingDown = (e) => {
    e.preventDefault()
    const ctx = insuredHandwritingCtxRef.current
    if (!ctx) return
    insuredHandwritingDrawingRef.current = true
    const p = point(e, insuredHandwritingCanvasRef)
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    ctx.lineTo(p.x + 0.01, p.y + 0.01)
    ctx.stroke()
    insuredHandwritingHasInkRef.current = true
  }

  const onHandwritingMove = (e) => {
    if (!handwritingDrawingRef.current || !handwritingCtxRef.current) return
    e.preventDefault()
    const p = point(e, handwritingCanvasRef)
    handwritingCtxRef.current.lineTo(p.x, p.y)
    handwritingCtxRef.current.stroke()
  }

  const onInsuredHandwritingMove = (e) => {
    if (!insuredHandwritingDrawingRef.current || !insuredHandwritingCtxRef.current) return
    e.preventDefault()
    const p = point(e, insuredHandwritingCanvasRef)
    insuredHandwritingCtxRef.current.lineTo(p.x, p.y)
    insuredHandwritingCtxRef.current.stroke()
  }

  const endHandwriting = () => {
    handwritingDrawingRef.current = false
    try { handwritingCtxRef.current?.closePath() } catch {}
  }

  const endInsuredHandwriting = () => {
    insuredHandwritingDrawingRef.current = false
    try { insuredHandwritingCtxRef.current?.closePath() } catch {}
  }

  const completeInPerson = async () => {
    const needsInsuredHandwriting = signing?.role === 'CONTRACT_HOLDER' && Boolean(form.insuredSameAsContractor)
    if (!signing?.signatureId || !hasInkRef.current) {
      show?.('서명을 입력해 주세요.', 'error')
      return
    }
    if (!handwritingHasInkRef.current) {
      show?.(`자필 문구 "${handwritingLabel(signing.role)}"를 입력해 주세요.`, 'error')
      return
    }
    if (needsInsuredHandwriting && !insuredHandwritingHasInkRef.current) {
      show?.('피보험자 자필 문구 "성실히 고지"를 입력해 주세요.', 'error')
      return
    }
    setBusy(true)
    try {
      await api.post(`/after-contract/certificates/signatures/${signing.signatureId}/complete`, {
        dataUrl: canvasRef.current.toDataURL('image/png'),
        handwritingDataUrl: handwritingCanvasRef.current.toDataURL('image/png'),
        additionalInsuredHandwritingDataUrl: needsInsuredHandwriting
          ? insuredHandwritingCanvasRef.current.toDataURL('image/png')
          : null,
      })
      setSigning(null)
      await loadSession(selectedSession.id)
      show?.('대면 서명을 저장했습니다.', 'success')
    } catch {
      show?.('대면 서명 저장에 실패했습니다.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const download = async () => {
    if (!selectedSession?.id) return
    await downloadSession(selectedSession)
  }

  const resetWriteForm = async () => {
    setSigning(null)
    const sp = new URLSearchParams(searchParams.toString())
    sp.delete('sessionId')
    setSearchParams(sp, { replace: true })

    let meExpl = ''
    let meConf = ''
    try {
      const res = await api.get('/users/me', { __silent: true })
      const me = unwrap(res) || {}
      meExpl = me.afterContractExplanationFileId || ''
      meConf = me.afterContractConfirmFileId || ''
    } catch {}

    setSelected(null)
    setSelectedId(null)
    setSignatureModeDraft({})
    setSignatureStatusPersonTab('CONTRACT_HOLDER')
    setPlannerEditing(false)
    setWriteStep(1)

    const base = initialForm(sp)
    setForm({
      ...base,
      plannerHandwriting_explanation: meExpl,
      plannerHandwriting_confirm: meConf,
    })
    show?.('작성 화면을 초기화했습니다. 필요하면 [이력] 탭에서 다시 불러올 수 있습니다.', 'success')
  }

  const downloadSession = async (session) => {
    if (!session?.id) return
    setBusy(true)
    try {
      const res = await api.get(`/after-contract/certificates/${session.id}/download`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }))
      const a = document.createElement('a')
      a.href = url
      const n = contractLineCountForDownload(session)
      const multi = n > 1
      a.download = `${session.contractorName || '고객'}_3종확인서${multi ? `_${n}건` : ''}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      show?.('다운로드에 실패했습니다. 3종 템플릿 등록 여부를 확인해 주세요.', 'error')
    } finally {
      setBusy(false)
    }
  }

  const updatePrintedAt = (value) => {
    const printedAt = normalizeDateInputValue(value)
    setForm((prev) => ({
      ...prev,
      printedAt,
      afterContractLines: (prev.afterContractLines || [emptyContractLine(printedAt)]).map((row) => ({
        ...row,
        printedAt,
      })),
    }))
  }

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value }
      if (prev.insuredSameAsContractor) {
        if (key === 'contractorName') next.insuredName = value
        if (key === 'contractorPhone') next.insuredPhone = value
      }
      return next
    })
  }

  const updateSameAsContractor = (checked) => {
    setForm((prev) => ({
      ...prev,
      insuredSameAsContractor: checked,
      insuredName: checked ? prev.contractorName : prev.insuredName,
      insuredPhone: checked ? prev.contractorPhone : prev.insuredPhone,
    }))
    setSignatureStatusPersonTab('CONTRACT_HOLDER')
  }

  const updateContractLine = (index, key, value) => {
    setForm((prev) => {
      const rows = [...(prev.afterContractLines || [emptyContractLine()])]
      if (!rows[index]) rows[index] = emptyContractLine()
      rows[index] = { ...rows[index], [key]: value }
      return { ...prev, afterContractLines: rows }
    })
  }

  const addContractLine = () => {
    setForm((prev) => ({
      ...prev,
      afterContractLines: [...(prev.afterContractLines || [emptyContractLine(prev.printedAt)]), emptyContractLine(prev.printedAt)],
    }))
  }

  const removeContractLine = (index) => {
    setForm((prev) => {
      const rows = [...(prev.afterContractLines || [emptyContractLine()])]
      if (rows.length <= 1) return prev
      rows.splice(index, 1)
      return { ...prev, afterContractLines: rows }
    })
  }

  useEffect(() => {
    if (!selected) return
    const extra = selected.extraFields || {}
    setSignatureModeDraft(extra.signatureCollectionModes || {})
    const sessionExpl = extra.plannerHandwriting_explanation || extra.customerHandwriting_explanation || ''
    const sessionConf = extra.plannerHandwriting_confirm || extra.customerHandwriting_confirm || ''

    const afterContractLines = linesFromSessionExtra(selected)
    const loadedPrintedAt =
      normalizeDateInputValue(extra.printedAt || extra.documentDate) ||
      normalizeDateInputValue(afterContractLines[0]?.printedAt || afterContractLines[0]?.documentDate) ||
      todayDateInputValue()

    setForm((prev) => ({
      ...prev,
      customerRefId: selected.customerRefId != null ? String(selected.customerRefId) : '',
      contractId: selected.contractId != null ? String(selected.contractId) : '',
      applicationNumber: selected.applicationNumber || '',
      policyNumber: selected.policyNumber || '',
      designNumber: selected.designNumber || '',
      insuranceCompanyName: selected.insuranceCompanyName || '',
      productName: selected.productName || '',
      contractorName: selected.contractorName || '',
      contractorPhone: selected.contractorPhone || '',
      insuredSameAsContractor: extra.insuredSameAsContractor !== false,
      insuredName: selected.insuredName || '',
      insuredPhone: selected.insuredPhone || '',
      plannerHandwriting_explanation: sessionExpl || prev.plannerHandwriting_explanation,
      plannerHandwriting_confirm: sessionConf || prev.plannerHandwriting_confirm,
      printedAt: loadedPrintedAt,
      afterContractLines,
      contractDate: '',
      contractorBirthDate: '',
      insuredBirthDate: '',
    }))

    let ignore = false
    ;(async () => {
      let meExpl = ''
      let meConf = ''
      try {
        const res = await api.get('/users/me', { __silent: true })
        const me = unwrap(res) || {}
        meExpl = me.afterContractExplanationFileId || ''
        meConf = me.afterContractConfirmFileId || ''
      } catch {}
      if (ignore) return
      setForm((prev) => ({
        ...prev,
        plannerHandwriting_explanation: sessionExpl || meExpl || prev.plannerHandwriting_explanation || '',
        plannerHandwriting_confirm: sessionConf || meConf || prev.plannerHandwriting_confirm || '',
      }))
    })()
    return () => {
      ignore = true
    }
  }, [selected])

  const handleLinkSendClick = (role) => {
    if (role === 'INSURED' && form.insuredSameAsContractor) {
      show?.('계약자/피보험자 동일 체크 시 피보험자는 계약자 서명 방식으로 함께 처리됩니다.', 'error')
      return
    }
    if (!String(phoneForRemoteRole(form, role) || '').trim()) {
      show?.(`${ROLE_LABELS[role]} 휴대폰 번호를 입력한 뒤 링크 발송을 진행해 주세요.`, 'error')
      return
    }
    void prepareSignature(role, 'REMOTE')
  }

  return (
    <section className={embedded ? 'space-y-5' : 'mx-auto max-w-6xl px-4 py-8 space-y-5'}>
      <div className={`flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between ${embedded ? 'sr-only' : ''}`}>
        <div>
        <h1 className="text-2xl font-bold">3종확인서 작성</h1>
        </div>
      </div>

      <div className="border-b flex gap-4 text-sm">
        <button
          type="button"
          className={`pb-2 border-b-2 ${
            activeTab === 'write' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
          }`}
          onClick={() => setActiveTab('write')}
        >
          작성
        </button>
        <button
          type="button"
          className={`pb-2 border-b-2 ${
            activeTab === 'history' ? 'border-brand-navy text-brand-navy' : 'border-transparent text-slate-500'
          }`}
          onClick={() => setActiveTab('history')}
        >
          이력
        </button>
      </div>

      {activeTab === 'write' && (
        <div className="flex justify-end rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => resetWriteForm().catch(() => {})}
            className="shrink-0 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
          >
            초기화
          </button>
        </div>
      )}

      {activeTab === 'write' ? (
      <section className="rounded-lg border bg-white p-4 shadow-sm space-y-4">
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {WRITE_WIZARD_STEPS.map((s) => {
            const active = writeStep === s.id
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setWriteStep(s.id)}
                className={`rounded border px-3 py-2.5 text-left transition ${
                  active
                    ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                    : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}
              >
                <div className="text-xs font-semibold text-slate-500">STEP {s.id}</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">{s.title}</div>
              </button>
            )
          })}
        </div>

        <div className="mb-2 flex flex-wrap items-start justify-between gap-3 border-l-4 border-brand-navy pl-3">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
              STEP {writeStep} / {WRITE_WIZARD_STEPS.length}
            </div>
            <div className="text-base font-bold text-slate-900">
              {WRITE_WIZARD_STEPS.find((x) => x.id === writeStep)?.title}
            </div>
          </div>
          {writeStep === 1 && (
            <button
              type="button"
              disabled={busy}
              onClick={addContractLine}
              className="shrink-0 rounded border border-teal-200 bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
            >
              계약 추가
            </button>
          )}
        </div>

        <div className="min-h-[10rem] space-y-4">
          {writeStep === 1 && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <label className="block sm:max-w-xs">
                    <span className="text-xs font-semibold text-slate-700">확인서 기재일</span>
                    <input
                      type="date"
                      value={form.printedAt || ''}
                      onChange={(e) => updatePrintedAt(e.target.value)}
                      className="mt-1 w-full rounded border bg-white px-3 py-2 text-sm"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => updatePrintedAt(todayDateInputValue())}
                    className="shrink-0 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                  >
                    오늘
                  </button>
                </div>
              </div>
              {(form.afterContractLines || [emptyContractLine()]).map((row, idx) => (
                <div key={`contract-line-${idx}`} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-bold text-slate-800">계약 {idx + 1}</span>
                    {(form.afterContractLines || []).length > 1 && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => removeContractLine(idx)}
                        className="rounded border border-rose-200 bg-white px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                      >
                        이 계약 행 삭제
                      </button>
                    )}
                  </div>
                  <label className="mb-3 block md:max-w-xs">
                    <span className="text-xs font-semibold text-slate-700">확인서 기재일</span>
                    <input
                      type="date"
                      value={normalizeDateInputValue(row.printedAt) || form.printedAt || ''}
                      onChange={(e) => updateContractLine(idx, 'printedAt', e.target.value)}
                      className="mt-1 w-full rounded border bg-white px-3 py-2 text-sm"
                    />
                  </label>
                  <div className="grid gap-3 md:grid-cols-2">
                    {[
                      ['policyNumber', '증권번호'],
                      ['designNumber', '설계번호'],
                      ['insuranceCompanyName', '보험회사명'],
                      ['productName', '상품명'],
                    ].map(([fieldKey, lbl]) => (
                      <label key={`${idx}-${fieldKey}`} className="block">
                        <span className="text-xs font-semibold text-slate-700">{lbl}</span>
                        <input
                          value={row[fieldKey] || ''}
                          onChange={(e) => updateContractLine(idx, fieldKey, e.target.value)}
                          className="mt-1 w-full rounded border bg-white px-3 py-2 text-sm"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {writeStep === 2 && (
            <div className="space-y-6">
              <label className="flex items-start gap-2 rounded border border-slate-200 bg-white p-3 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={Boolean(form.insuredSameAsContractor)}
                  onChange={(e) => updateSameAsContractor(e.target.checked)}
                />
                <span className="font-semibold text-slate-800">계약자/피보험자 동일</span>
              </label>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700">계약자 성명</span>
                  <input
                    value={form.contractorName || ''}
                    onChange={(e) => updateField('contractorName', e.target.value)}
                    className="mt-1 w-full rounded border px-3 py-2 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700">계약자 휴대폰번호</span>
                  <input
                    value={form.contractorPhone || ''}
                    onChange={(e) => updateField('contractorPhone', e.target.value)}
                    className="mt-1 w-full rounded border px-3 py-2 text-sm"
                    placeholder="01012345678"
                    inputMode="tel"
                  />
                  <p className="mt-1 text-xs text-slate-500">링크 발송을 통한 서명을 할 경우에만 필요합니다.</p>
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700">피보험자 성명</span>
                  <input
                    value={form.insuredName || ''}
                    onChange={(e) => updateField('insuredName', e.target.value)}
                    disabled={Boolean(form.insuredSameAsContractor)}
                    className="mt-1 w-full rounded border px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                  />
                  {Boolean(form.insuredSameAsContractor) && (
                    <span className="mt-1 inline-block rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500">계약자와 동일</span>
                  )}
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-700">피보험자 휴대폰번호</span>
                  <input
                    value={form.insuredPhone || ''}
                    onChange={(e) => updateField('insuredPhone', e.target.value)}
                    disabled={Boolean(form.insuredSameAsContractor)}
                    className="mt-1 w-full rounded border px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                    placeholder="01012345678"
                    inputMode="tel"
                  />
                  <p className="mt-1 text-xs text-slate-500">링크 발송을 통한 서명을 할 경우에만 필요합니다.</p>
                  {Boolean(form.insuredSameAsContractor) && (
                    <span className="mt-1 inline-block rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500">계약자 번호 사용</span>
                  )}
                </label>
              </div>

              <div className="border-t border-slate-200 pt-6">
                <div className="grid gap-4 md:grid-cols-2 md:items-start">
              <div className="rounded border bg-white p-3">
                <div className="border-l-4 border-brand-navy pl-3">
                  <div className="text-sm font-bold text-slate-900">서명 방식</div>
                </div>
                <p className="mt-2 text-xs text-slate-500">고객에게 적용할 서명 수집 방식을 선택합니다.</p>
                {!form.insuredSameAsContractor && (
                  <div className="mt-3 flex gap-1 border-b border-slate-200 text-xs">
                    <button
                      type="button"
                      className={`flex-1 pb-2 font-semibold ${signatureStatusPersonTab === 'CONTRACT_HOLDER' ? 'border-b-2 border-brand-navy text-brand-navy' : 'text-slate-500'}`}
                      onClick={() => setSignatureStatusPersonTab('CONTRACT_HOLDER')}
                    >
                      계약자
                    </button>
                    <button
                      type="button"
                      className={`flex-1 pb-2 font-semibold ${signatureStatusPersonTab === 'INSURED' ? 'border-b-2 border-brand-navy text-brand-navy' : 'text-slate-500'}`}
                      onClick={() => setSignatureStatusPersonTab('INSURED')}
                    >
                      피보험자
                    </button>
                  </div>
                )}
                {(form.insuredSameAsContractor ? ['CONTRACT_HOLDER'] : [signatureStatusPersonTab]).map((role) => {
                  const sig = signatureFor(selectedSession, role)
                  const selectedMode =
                    signatureModeDraft?.[role] || selectedSession?.extraFields?.signatureCollectionModes?.[role] || sig?.mode || ''
                  const printMode = selectedMode === 'PRINT'
                  return (
                    <div key={role} className="mt-3 rounded border border-slate-200 p-3">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold">{ROLE_LABELS[role]}</div>
                        <div className="text-xs text-slate-500">{printMode ? '출력 후 자필' : statusLabel(sig?.status)}</div>
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">선택 방식: {signatureModeLabel(selectedMode)}</div>
                      <div className="mt-3 grid gap-2">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleLinkSendClick(role)}
                          className={`rounded border px-3 py-2 text-left text-xs ${selectedMode === 'REMOTE' ? 'border-slate-300 bg-white text-brand-navy' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                        >
                          <span className="block font-semibold">링크발송</span>
                          <span className="mt-0.5 block text-[11px] font-normal text-slate-500">알림톡으로 서명 링크 전송</span>
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void prepareSignature(role, 'IN_PERSON')}
                          className={`rounded border px-3 py-2 text-left text-xs ${selectedMode === 'IN_PERSON' ? 'border-slate-300 bg-white text-brand-navy' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                        >
                          <span className="block font-semibold">대면서명</span>
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void setSignatureCollectionMode(role, 'PRINT')}
                          className={`rounded border px-3 py-2 text-left text-xs ${printMode ? 'border-slate-300 bg-white text-brand-navy' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                        >
                          <span className="block font-semibold">자필서명</span>
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="rounded border bg-white p-3">
                <div className="border-l-4 border-brand-navy pl-3">
                  <div className="text-sm font-bold text-slate-900">입력된 서명 확인</div>
                </div>
                <p className="mt-2 text-xs text-slate-500">저장된 계약자·피보험자 서명 및 자필 이미지를 이 화면에서 바로 확인합니다.</p>
                {!selectedSession?.id && (
                  <div className="mt-4 rounded border border-dashed border-slate-200 bg-slate-50 p-4 text-xs text-slate-500">
                    세션을 저장하거나 불러온 뒤 확인할 수 있습니다.
                  </div>
                )}
                {selectedSession?.id && step3SignaturePreview.loading && (
                  <div className="mt-4 rounded border border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-500">서명·자필 이미지를 불러오는 중입니다.</div>
                )}
                {selectedSession?.id && !step3SignaturePreview.loading && step3SignaturePreview.items.length === 0 && (
                  <div className="mt-4 rounded border border-dashed border-slate-200 bg-slate-50 p-4 text-xs text-slate-500">아직 확인할 수 있는 고객 서명·자필이 없습니다.</div>
                )}
                {selectedSession?.id && !step3SignaturePreview.loading && step3SignaturePreview.items.length > 0 && (
                  <div className="mt-4 space-y-4">
                    {step3SignaturePreview.items.map((item, idx) => (
                      <div key={`${item.label}-${idx}`} className="rounded border border-slate-200 bg-slate-50/80 p-3">
                        <div className="text-xs font-semibold text-slate-700">{item.label}</div>
                        {item.url ? (
                          <img src={item.url} alt="" className="mt-2 max-h-48 w-auto rounded border bg-white p-2" />
                        ) : (
                          <div className="mt-2 text-xs text-rose-600">이미지를 불러오지 못했습니다.</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
                </div>
              </div>
            </div>
          )}

          {writeStep === 3 && (
            <>
              <div className="rounded border border-slate-200 bg-white p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm font-bold text-slate-900">설계사 자필</div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" disabled={busy} onClick={loadPlannerPreset} className="rounded border bg-white px-3 py-2 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50">
                      저장 자필 불러오기
                    </button>
                    <button type="button" disabled={busy} onClick={() => setPlannerEditing(true)} className="rounded border bg-white px-3 py-2 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50">
                      자필 새로 작성
                    </button>
                  </div>
                </div>
                <div className="mt-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={goToMyProfileAfterAutosave}
                    className="text-xs font-semibold text-brand-navy underline-offset-2 hover:underline disabled:opacity-50"
                  >
                    내 정보에서 서명·자필 등록
                  </button>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div>
                    <div className="mb-1 text-xs font-semibold text-slate-600">설명 자필</div>
                    {plannerPreviewUrls.explanation && (
                      <div className="mb-2 rounded border bg-white p-2">
                        <img src={plannerPreviewUrls.explanation} alt="저장된 설명 자필" className="max-h-16" />
                      </div>
                    )}
                    {!plannerPreviewUrls.explanation && !plannerEditing && (
                      <div className="rounded border border-dashed bg-slate-50 p-4 text-xs text-slate-500">저장된 설명 자필이 없습니다.</div>
                    )}
                    {plannerEditing && (
                      <>
                        <canvas
                          ref={plannerExplanationCanvasRef}
                          className="block w-full touch-none rounded-xl border bg-white"
                          onPointerDown={(e) => onPlannerHandwritingDown(e, plannerExplanationCanvasRef, plannerExplanationCtxRef, plannerExplanationDrawingRef, plannerExplanationHasInkRef)}
                          onPointerMove={(e) => onPlannerHandwritingMove(e, plannerExplanationCanvasRef, plannerExplanationCtxRef, plannerExplanationDrawingRef)}
                          onPointerUp={() => endPlannerHandwriting(plannerExplanationCtxRef, plannerExplanationDrawingRef)}
                          onPointerLeave={() => endPlannerHandwriting(plannerExplanationCtxRef, plannerExplanationDrawingRef)}
                          onPointerCancel={() => endPlannerHandwriting(plannerExplanationCtxRef, plannerExplanationDrawingRef)}
                        />
                        <button type="button" className="mt-2 rounded border bg-white px-3 py-1.5 text-xs hover:bg-slate-50" onClick={() => clearPlannerCanvas(plannerExplanationCanvasRef, plannerExplanationCtxRef, plannerExplanationHasInkRef, 'plannerHandwriting_explanation')}>
                          설명 지우기
                        </button>
                      </>
                    )}
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-semibold text-slate-600">확인 자필</div>
                    {plannerPreviewUrls.confirm && (
                      <div className="mb-2 rounded border bg-white p-2">
                        <img src={plannerPreviewUrls.confirm} alt="저장된 확인 자필" className="max-h-16" />
                      </div>
                    )}
                    {!plannerPreviewUrls.confirm && !plannerEditing && (
                      <div className="rounded border border-dashed bg-slate-50 p-4 text-xs text-slate-500">저장된 확인 자필이 없습니다.</div>
                    )}
                    {plannerEditing && (
                      <>
                        <canvas
                          ref={plannerConfirmCanvasRef}
                          className="block w-full touch-none rounded-xl border bg-white"
                          onPointerDown={(e) => onPlannerHandwritingDown(e, plannerConfirmCanvasRef, plannerConfirmCtxRef, plannerConfirmDrawingRef, plannerConfirmHasInkRef)}
                          onPointerMove={(e) => onPlannerHandwritingMove(e, plannerConfirmCanvasRef, plannerConfirmCtxRef, plannerConfirmDrawingRef)}
                          onPointerUp={() => endPlannerHandwriting(plannerConfirmCtxRef, plannerConfirmDrawingRef)}
                          onPointerLeave={() => endPlannerHandwriting(plannerConfirmCtxRef, plannerConfirmDrawingRef)}
                          onPointerCancel={() => endPlannerHandwriting(plannerConfirmCtxRef, plannerConfirmDrawingRef)}
                        />
                        <button type="button" className="mt-2 rounded border bg-white px-3 py-1.5 text-xs hover:bg-slate-50" onClick={() => clearPlannerCanvas(plannerConfirmCanvasRef, plannerConfirmCtxRef, plannerConfirmHasInkRef, 'plannerHandwriting_confirm')}>
                          확인 지우기
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {plannerEditing && (
                  <div className="mt-3 flex justify-end gap-2">
                    <button type="button" disabled={busy} onClick={() => setPlannerEditing(false)} className="rounded border px-3 py-2 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50">
                      취소
                    </button>
                    <button type="button" disabled={busy} onClick={savePlannerPreset} className="rounded bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
                      작성한 자필 저장
                    </button>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button disabled={busy} onClick={saveSession} className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
                  저장
                </button>
                <button
                  disabled={!selectedSession?.id || busy}
                  onClick={download}
                  className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                >
                  3종 다운로드
                </button>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
          <button
            type="button"
            disabled={busy || writeStep <= 1}
            onClick={() => setWriteStep((w) => Math.max(1, w - 1))}
            className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-40"
          >
            이전
          </button>
          {writeStep < WRITE_WIZARD_STEPS.length ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setWriteStep((w) => Math.min(WRITE_WIZARD_STEPS.length, w + 1))}
              className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              다음
            </button>
          ) : (
            <span />
          )}
        </div>
      </section>
      ) : (
        <section className="rounded-lg border bg-white p-4 shadow-sm space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-slate-900">작성 이력</h2>
              <p className="mt-1 text-sm text-slate-500">
                이전에 작성한 3종확인서를 다시 불러오거나 다운로드할 수 있습니다.
              </p>
            </div>
            <button type="button" disabled={busy} onClick={() => loadSessions().catch(() => {})} className="rounded border px-3 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">
              새로고침
            </button>
          </div>

          <div className="mt-5 space-y-3">
            {sessions.map((s) => (
              <div key={s.id} className={`rounded border bg-white p-3 ${String(s.id) === String(selectedSession?.id) ? 'border-slate-300' : 'border-slate-200'}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">{s.contractorName || '이름 없음'}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {(() => {
                        const n = contractLineCountForDownload(s)
                        if (n > 1) {
                          return (
                            <>
                              계약 {n}건 · {s.insuranceCompanyName || '-'} · {s.productName || '-'} ·{' '}
                              {s.policyNumber || '증권번호 없음'}
                            </>
                          )
                        }
                        return (
                          <>
                            {s.insuranceCompanyName || '-'} · {s.productName || '-'} ·{' '}
                            {s.policyNumber || '증권번호 없음'}
                          </>
                        )
                      })()}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      최근 수정: {formatMenuInstant(s, 'updatedAt')}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={async () => {
                        await loadSession(s.id)
                        setWriteStep(1)
                        setActiveTab('write')
                      }}
                      className="rounded border px-3 py-2 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
                    >
                      불러오기
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => downloadSession(s)}
                      className="rounded bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      다운로드
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {sessions.length === 0 && (
              <div className="rounded border border-dashed bg-slate-50 p-8 text-center text-sm text-slate-500">
                작성 이력이 없습니다.
              </div>
            )}
          </div>
        </section>
      )}

      {signing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-4 shadow-xl sm:p-5">
            <div className="text-base font-bold sm:text-lg">{ROLE_LABELS[signing.role]} 대면 서명</div>
            <div className="mt-1.5 text-xs text-slate-600 sm:mt-2 sm:text-sm">
              고객에게 화면을 제시하고 서명과 자필 문구를 각각 받아주세요.
            </div>
            <div className="mt-3">
              <div className="mb-0.5 text-xs font-semibold text-slate-600">{ROLE_LABELS[signing.role]} 서명</div>
              <canvas ref={canvasRef} className="block w-full touch-none rounded-lg border bg-white sm:rounded-xl" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={end} onPointerLeave={end} onPointerCancel={end} />
            </div>
            <div className="mt-3">
              <div className="mb-0.5 text-xs font-semibold text-slate-600">
                {ROLE_LABELS[signing.role]} 자필: “{handwritingLabel(signing.role)}”
              </div>
              <canvas ref={handwritingCanvasRef} className="block w-full touch-none rounded-lg border bg-white sm:rounded-xl" onPointerDown={onHandwritingDown} onPointerMove={onHandwritingMove} onPointerUp={endHandwriting} onPointerLeave={endHandwriting} onPointerCancel={endHandwriting} />
            </div>
            {signing.role === 'CONTRACT_HOLDER' && form.insuredSameAsContractor && (
              <div className="mt-3">
                <div className="mb-0.5 text-xs font-semibold text-slate-600">
                  피보험자 자필: “성실히 고지”
                </div>
                <canvas ref={insuredHandwritingCanvasRef} className="block w-full touch-none rounded-lg border bg-white sm:rounded-xl" onPointerDown={onInsuredHandwritingDown} onPointerMove={onInsuredHandwritingMove} onPointerUp={endInsuredHandwriting} onPointerLeave={endInsuredHandwriting} onPointerCancel={endInsuredHandwriting} />
              </div>
            )}
            <div className="mt-3 flex flex-wrap justify-end gap-1.5 sm:mt-4 sm:gap-2">
              <button onClick={() => setSigning(null)} className="rounded border px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm">닫기</button>
              <button onClick={clearCanvas} className="rounded border px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm">전체 지우기</button>
              <button onClick={clearHandwritingCanvas} className="rounded border px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm">자필만 지우기</button>
              {signing.role === 'CONTRACT_HOLDER' && form.insuredSameAsContractor && (
                <button onClick={clearInsuredHandwritingCanvas} className="rounded border px-3 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm">피보험자 자필 지우기</button>
              )}
              <button disabled={busy} onClick={completeInPerson} className="rounded bg-brand-navy px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm">저장</button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
